import React, { useEffect, useMemo, useState } from 'react';
import { Zap, AlertTriangle, Save, Check, Guitar, Link2, X as XIcon, Mic2 } from 'lucide-react';
import { Button, Card, CardContent, FormField, Badge } from '../ui';
import { fetchWithCsrf } from '@utils/fetchWithCsrf';
import { useProductionConfig } from '@/context/ProductionConfigContext';
import { usePersona } from '@/hooks/usePersona';

interface BulkRunConfig {
  id?: string;
  userId?: string;
  enabled: boolean;
  targetCount: number;
  budgetCapUsd: number;
  minDelaySeconds: number;
  presetStrategy: 'weighted' | 'uniform';
  styleMode: 'studio' | 'live';
  maxConsecutiveErrors: number;
  sessionCompleted?: number;
  sessionFailed?: number;
  sessionSpentUsd?: number;
  sessionStartedAt?: string | null;
  lastRunAt?: string | null;
  lastError?: string | null;
  instrumentForgeConfigJson?: string | null;
  personaSnapshotJson?: string | null;
}

const DEFAULT_CONFIG: BulkRunConfig = {
  enabled: false,
  targetCount: 10,
  budgetCapUsd: 5.0,
  minDelaySeconds: 60,
  presetStrategy: 'weighted',
  styleMode: 'studio',
  maxConsecutiveErrors: 3,
  instrumentForgeConfigJson: null,
  personaSnapshotJson: null,
};

export const BulkRunsTab: React.FC = () => {
  const { instrumentForgeConfig, instrumentForgeOutput } = useProductionConfig();
  const { activePersona } = usePersona();
  const [config, setConfig] = useState<BulkRunConfig>(DEFAULT_CONFIG);
  const [initial, setInitial] = useState<BulkRunConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const res = await fetchWithCsrf('/api/bulk-runs/config');
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json() as BulkRunConfig;
        if (cancelled) return;
        setConfig(prev => {
          // Only overwrite server-tracked fields; don't stomp user's local edits
          return {
            ...prev,
            id: data.id,
            userId: data.userId,
            sessionCompleted: data.sessionCompleted,
            sessionFailed: data.sessionFailed,
            sessionSpentUsd: data.sessionSpentUsd,
            sessionStartedAt: data.sessionStartedAt,
            lastRunAt: data.lastRunAt,
            lastError: data.lastError,
            // First load: also seed the editable fields
            ...(initial === null ? data : {}),
          };
        });
        if (initial === null) setInitial(data);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load config');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    void load();
    // Poll every 10s while enabled so live status stays fresh
    const poll = setInterval(() => { if (config.enabled) void load(); }, 10_000);
    return () => { cancelled = true; clearInterval(poll); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.enabled]);

  const dirty = initial ? JSON.stringify({ ...initial, enabled: undefined }) !== JSON.stringify({ ...config, enabled: undefined }) : true;
  const toggleDirty = initial ? initial.enabled !== config.enabled : true;

  const linkedInstrumentForge = useMemo(() => {
    if (!config.instrumentForgeConfigJson) return null;
    try {
      const parsed = JSON.parse(config.instrumentForgeConfigJson) as { profileAKey: string | null; profileBKey: string | null; blendRatio: number };
      if (!parsed.profileAKey) return null;
      return parsed.profileBKey
        ? `${parsed.profileAKey} + ${parsed.profileBKey} (${parsed.blendRatio}%)`
        : parsed.profileAKey;
    } catch { return null; }
  }, [config.instrumentForgeConfigJson]);

  const linkedPersona = useMemo(() => {
    if (!config.personaSnapshotJson) return null;
    try {
      const parsed = JSON.parse(config.personaSnapshotJson) as { name: string; useLockedTemplate: boolean };
      return parsed.name || null;
    } catch { return null; }
  }, [config.personaSnapshotJson]);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetchWithCsrf('/api/bulk-runs/config', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || body.message || `HTTP ${res.status}`);
      }
      const data = await res.json() as BulkRunConfig;
      setConfig(data);
      setInitial(data);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleEnabled = () => setConfig({ ...config, enabled: !config.enabled });

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-accent text-slate-950 flex items-center justify-center">
          <Zap size={20} />
        </div>
        <div>
          <h2 className="text-lg font-bold text-slate-100">Bulk Runs</h2>
          <p className="text-xs text-slate-400">
            Walk-away generation: flip the toggle, come back to N songs cooked with your top-rated presets.
          </p>
        </div>
      </div>

      {/* Executor status banner */}
      <div className="flex items-start gap-3 p-4 rounded-xl border border-slate-700 bg-slate-900/40 text-xs text-slate-300">
        <AlertTriangle size={16} className="shrink-0 mt-0.5 text-accent" />
        <div>
          <strong className="text-slate-100">Live.</strong> Toggle ON = the server picks a preset every {config.minDelaySeconds}s, runs Studio Team, saves the song to your Vault tagged <code className="font-mono text-slate-400">bulk-run</code>, and stops when it hits your count or budget cap. Consecutive errors auto-flip the toggle OFF. Requires your Claude key to be set in Settings.
        </div>
      </div>

      {/* Master toggle card */}
      <Card variant="outlined" className={config.enabled ? 'border-accent/40 bg-accent/5' : 'border-slate-800'}>
        <CardContent>
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-bold text-slate-100">Master Toggle</div>
              <div className="text-xs text-slate-400 mt-1">
                When on (v1+), the server picks a preset, runs Studio Team, saves the song to Vault, waits {config.minDelaySeconds}s, repeats. Stops on count or budget cap.
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={config.enabled}
              onClick={toggleEnabled}
              disabled={loading}
              className={`relative w-14 h-8 rounded-full transition-colors shrink-0 ${config.enabled ? 'bg-accent' : 'bg-slate-700'} disabled:opacity-50`}
            >
              <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-slate-950 transition-transform ${config.enabled ? 'translate-x-6' : ''}`} />
            </button>
          </div>

          {/* Session status (visible if we have a session in progress) */}
          {(config.sessionStartedAt || (config.sessionCompleted ?? 0) > 0) && (
            <div className="mt-4 pt-4 border-t border-slate-800/60 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Completed</div>
                <div className="text-slate-100 font-mono">{config.sessionCompleted ?? 0} / {config.targetCount}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Spent</div>
                <div className="text-slate-100 font-mono">${(config.sessionSpentUsd ?? 0).toFixed(2)} / ${config.budgetCapUsd.toFixed(2)}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Failed</div>
                <div className={`font-mono ${(config.sessionFailed ?? 0) > 0 ? 'text-red-400' : 'text-slate-100'}`}>{config.sessionFailed ?? 0}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest">Last Run</div>
                <div className="text-slate-100 font-mono">{config.lastRunAt ? new Date(config.lastRunAt).toLocaleTimeString() : '—'}</div>
              </div>
              {config.lastError && (() => {
                // target_reached and budget_reached are graceful stops, not errors.
                const isGraceful = /target_reached|budget_reached/.test(config.lastError);
                return (
                  <div className={`col-span-full text-[11px] mt-1 ${isGraceful ? 'text-emerald-300' : 'text-red-300'}`}>
                    <strong>{isGraceful ? 'Session complete:' : 'Last error:'}</strong> {config.lastError}
                  </div>
                );
              })()}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Config form */}
      <Card variant="outlined" className="border-slate-800">
        <CardContent>
          <h3 className="text-sm font-bold text-slate-100 mb-4">Session Limits</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FormField label={`Target Song Count: ${config.targetCount}`}>
              <input
                type="range"
                min={1}
                max={50}
                step={1}
                value={config.targetCount}
                onChange={(e) => setConfig({ ...config, targetCount: parseInt(e.target.value, 10) })}
                className="w-full accent-accent"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>1</span>
                <span>25</span>
                <span>50</span>
              </div>
            </FormField>

            <FormField label={`Budget Cap: $${config.budgetCapUsd.toFixed(2)}`}>
              <input
                type="range"
                min={0.5}
                max={30}
                step={0.5}
                value={config.budgetCapUsd}
                onChange={(e) => setConfig({ ...config, budgetCapUsd: parseFloat(e.target.value) })}
                className="w-full accent-accent"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>$0.50</span>
                <span>$15</span>
                <span>$30</span>
              </div>
            </FormField>

            <FormField label={`Min Delay Between Runs: ${config.minDelaySeconds}s`}>
              <input
                type="range"
                min={30}
                max={600}
                step={30}
                value={config.minDelaySeconds}
                onChange={(e) => setConfig({ ...config, minDelaySeconds: parseInt(e.target.value, 10) })}
                className="w-full accent-accent"
              />
              <div className="flex justify-between text-[10px] text-slate-500 mt-1">
                <span>30s</span>
                <span>5min</span>
                <span>10min</span>
              </div>
            </FormField>

            <FormField label={`Auto-Disable After ${config.maxConsecutiveErrors} Consecutive Errors`}>
              <input
                type="range"
                min={1}
                max={10}
                step={1}
                value={config.maxConsecutiveErrors}
                onChange={(e) => setConfig({ ...config, maxConsecutiveErrors: parseInt(e.target.value, 10) })}
                className="w-full accent-accent"
              />
            </FormField>
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-bold text-slate-100 mb-3">Preset Selection Strategy</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${config.presetStrategy === 'weighted' ? 'border-accent/60 bg-accent/5' : 'border-slate-800 hover:border-slate-700'}`}>
                <input
                  type="radio"
                  name="strategy"
                  value="weighted"
                  checked={config.presetStrategy === 'weighted'}
                  onChange={() => setConfig({ ...config, presetStrategy: 'weighted' })}
                  className="mt-1 accent-accent"
                />
                <div>
                  <div className="text-xs font-bold text-slate-100 flex items-center gap-2">
                    Weighted <Badge variant="success-muted" size="xs">Recommended</Badge>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Uses the Vault rating loop. 👍-rated presets surface more often, 👎-rated less. New presets stay at baseline weight.
                  </div>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${config.presetStrategy === 'uniform' ? 'border-accent/60 bg-accent/5' : 'border-slate-800 hover:border-slate-700'}`}>
                <input
                  type="radio"
                  name="strategy"
                  value="uniform"
                  checked={config.presetStrategy === 'uniform'}
                  onChange={() => setConfig({ ...config, presetStrategy: 'uniform' })}
                  className="mt-1 accent-accent"
                />
                <div>
                  <div className="text-xs font-bold text-slate-100">Uniform Random</div>
                  <div className="text-[11px] text-slate-400 mt-1">
                    Ignore ratings — every preset has equal chance. Useful for exploration without bias.
                  </div>
                </div>
              </label>
            </div>
          </div>

          <div className="mt-6" title='Studio forbids "live" language in style tags entirely (Suno reads any mention of "live" as a request for concert/crowd atmosphere, even innocuous phrasing like "live room feel"). Live explicitly wants that concert character.'>
            <h3 className="text-sm font-bold text-slate-100 mb-3">Style</h3>
            <div className="flex bg-slate-800 border border-slate-700 rounded-full p-0.5 w-max">
              {(['studio', 'live'] as const).map(mode => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setConfig({ ...config, styleMode: mode })}
                  className={`px-4 py-1.5 rounded-full text-[11px] font-black uppercase tracking-widest transition-all ${
                    config.styleMode === mode ? 'bg-accent text-slate-950' : 'text-slate-400 hover:text-slate-200'
                  }`}
                >
                  {mode}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-bold text-slate-100 mb-2 flex items-center gap-2">
              <Guitar size={16} className="text-accent" /> InstrumentForge Physics
            </h3>
            <p className="text-[11px] text-slate-400 mb-3">
              Optional. Links your active InstrumentForge guitar/rhythm blend so every generated song uses your rig, not just the picked style preset's genre label.
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs">
                {linkedInstrumentForge ? (
                  <Badge variant="primary-muted" leftIcon={<Link2 size={12} />}>{linkedInstrumentForge}</Badge>
                ) : (
                  <span className="text-slate-500 italic">No InstrumentForge blend linked.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="xs"
                  disabled={!instrumentForgeConfig?.profileAKey}
                  onClick={() => setConfig({ ...config, instrumentForgeConfigJson: JSON.stringify(instrumentForgeConfig) })}
                  leftIcon={<Link2 size={12} />}
                >
                  {instrumentForgeOutput ? `Link current: ${instrumentForgeOutput.name}` : 'No active blend to link'}
                </Button>
                {config.instrumentForgeConfigJson && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfig({ ...config, instrumentForgeConfigJson: null })}
                    leftIcon={<XIcon size={12} />}
                  >
                    Unlink
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="text-sm font-bold text-slate-100 mb-2 flex items-center gap-2">
              <Mic2 size={16} className="text-accent" /> Locked Persona
            </h3>
            <p className="text-[11px] text-slate-400 mb-3">
              Optional. Links your active Persona so every generated song preserves its locked Style Prompt (BPM/key aside) and vocal/rhythm physics constraints — same as running Studio Team directly with this persona selected.
            </p>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="text-xs">
                {linkedPersona ? (
                  <Badge variant="primary-muted" leftIcon={<Link2 size={12} />}>{linkedPersona}</Badge>
                ) : (
                  <span className="text-slate-500 italic">No persona linked.</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  size="xs"
                  disabled={!activePersona?.useLockedTemplate}
                  onClick={() => setConfig({ ...config, personaSnapshotJson: JSON.stringify(activePersona) })}
                  leftIcon={<Link2 size={12} />}
                >
                  {activePersona?.useLockedTemplate ? `Link current: ${activePersona.name}` : 'No locked persona active'}
                </Button>
                {config.personaSnapshotJson && (
                  <Button
                    variant="ghost"
                    size="xs"
                    onClick={() => setConfig({ ...config, personaSnapshotJson: null })}
                    leftIcon={<XIcon size={12} />}
                  >
                    Unlink
                  </Button>
                )}
              </div>
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between gap-3">
            <div className="text-[11px] text-slate-500">
              {toggleDirty && <span className="text-amber-400 font-bold">Toggle changed — save to apply.</span>}
              {!toggleDirty && dirty && <span>Unsaved changes.</span>}
              {error && <span className="text-red-400"> · {error}</span>}
            </div>
            <Button
              variant={saved ? 'success' : 'primary'}
              size="sm"
              onClick={save}
              isLoading={saving}
              loadingText="Saving..."
              disabled={loading || (!dirty && !toggleDirty)}
              leftIcon={saved ? <Check size={14} /> : <Save size={14} />}
            >
              {saved ? 'Saved' : 'Save Config'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
